import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { MenuItem } from "@/types/Sidebar.type";
import { collectActiveMenuPaths, isPathAllowedByMenu } from "./menuAccess";
import { PageAccessDenied } from "./PageAccessDenied";
import { LoadingSpinner } from "./shell";

export interface MenuAccessGuardProps {
  menu: MenuItem[];
  /** When false, menu is still loading — do not show Access Denied. */
  menuReady?: boolean;
  roleLabel?: string;
  homePath?: string;
  children: React.ReactNode;
}

/**
 * Blocks address-bar / deep-link access to pages that are not in the
 * user's current sidebar menu. Renders {@link PageAccessDenied} instead.
 *
 * Never blocks during menu bootstrap (`menuReady === false` or empty menu).
 */
export const MenuAccessGuard: React.FC<MenuAccessGuardProps> = ({
  menu,
  menuReady = true,
  roleLabel,
  homePath = "/dashboard",
  children,
}) => {
  const location = useLocation();

  const hasMenuPaths = useMemo(
    () => collectActiveMenuPaths(menu).size > 0,
    [menu],
  );

  const allowed = useMemo(() => {
    if (!menuReady || !hasMenuPaths) {
      return true;
    }
    return isPathAllowedByMenu(location.pathname, menu);
  }, [location.pathname, menu, menuReady, hasMenuPaths]);

  if (!menuReady) {
    return <LoadingSpinner />;
  }

  if (!allowed) {
    return (
      <PageAccessDenied
        attemptedPath={location.pathname}
        roleLabel={roleLabel}
        homePath={homePath}
      />
    );
  }

  return <>{children}</>;
};
